<template>
  <v-row>
    <v-col cols="12">
      <v-btn
        variant="text"
        color="primary"
        class="mb-3"
        prepend-icon="mdi-arrow-left"
        @click="goBack"
      >
        {{ backLabel }}
      </v-btn>

      <v-card class="pa-4 rounded-lg" elevation="1">
        <div class="d-flex align-start justify-space-between mb-1">
          <div>
            <v-card-title
              class="text-h5 text-primary font-weight-bold pb-1 px-0"
            >
              Schedule Settings
            </v-card-title>

            <v-card-subtitle class="text-body-2 text-primary pb-4 px-0">
              {{ thermostatName || `Thermostat #${thermostatId}` }}
            </v-card-subtitle>
          </div>

          <div>
            <v-btn
              variant="outlined"
              color="primary"
              prepend-icon="mdi-restore"
              class="mt-1 mr-3"
              @click="resetSchedules"
            >
              Clear Schedule
            </v-btn>

            <v-btn
              variant="outlined"
              color="primary"
              prepend-icon="mdi-tray-arrow-down"
              class="mt-1"
              @click="showImportDialog = true"
            >
              Import Schedule
            </v-btn>
          </div>
        </div>

        <ImportScheduleDialog
          v-model="showImportDialog"
          :current-thermostat-id="thermostatId"
          :site-id="defaultSite && defaultSite.id"
          @import="handleImport"
        />

        <v-progress-linear
          v-if="isLoading"
          indeterminate
          color="primary"
          class="mb-4"
        />

        <div v-else>
          <v-expansion-panels
            v-model="openPlanPanels"
            multiple
            variant="accordion"
          >
            <v-expansion-panel
              v-for="plan in plans"
              :key="plan.plan_type"
              :title="plan.label"
            >
              <v-expansion-panel-text>
                <v-row>
                  <v-col cols="12" md="3">
                    <v-select
                      v-model="plan.temperature_control_mode"
                      :items="temperatureControlModeOptions"
                      item-title="title"
                      item-value="value"
                      label="Temperature Control Mode"
                      density="compact"
                      variant="outlined"
                    />
                  </v-col>

                  <v-col cols="12" md="2">
                    <v-select
                      v-model="plan.fan_mode"
                      :items="fanModeOptions"
                      item-title="title"
                      item-value="value"
                      label="Fan Mode"
                      density="compact"
                      variant="outlined"
                    />
                  </v-col>

                  <v-col cols="12" md="2">
                    <v-text-field
                      v-model.number="plan.target_temperature"
                      type="number"
                      step="1"
                      :min="plan.temperature_unit === 'C' ? 10 : 50"
                      :max="plan.temperature_unit === 'C' ? 28 : 82"
                      :label="
                        plan.temperature_unit === 'C'
                          ? 'Target Temperature (10 – 28 °C)'
                          : 'Target Temperature (50 – 82 °F)'
                      "
                      :error-messages="temperatureError(plan)"
                      density="compact"
                      variant="outlined"
                    />
                  </v-col>

                  <v-col cols="12" md="2">
                    <v-select
                      :model-value="plan.temperature_unit"
                      :items="temperatureUnitOptions"
                      label="Target Unit"
                      density="compact"
                      variant="outlined"
                      @update:model-value="
                        (newUnit) => onUnitChange(plan, newUnit)
                      "
                    />
                  </v-col>

                  <v-col cols="12" md="3">
                    <v-text-field
                      v-model.number="plan.temperature_tolerance"
                      type="number"
                      step="0.5"
                      min="0.5"
                      max="10"
                      label="Temperature Control Tolerance (0.5 – 10)"
                      :error-messages="toleranceError(plan)"
                      density="compact"
                      variant="outlined"
                    />
                  </v-col>
                </v-row>

                <v-alert
                  v-if="planValidationErrors[plan.plan_type]?.length"
                  type="error"
                  variant="tonal"
                  class="mb-4"
                >
                  <div
                    v-for="error in planValidationErrors[plan.plan_type]"
                    :key="error"
                  >
                    {{ error }}
                  </div>
                </v-alert>

                <v-table density="compact" class="schedule-table">
                  <thead>
                    <tr>
                      <th>
                        <span class="text-primary">#</span>
                      </th>
                      <th>
                        <span class="text-primary">Enabled</span>
                      </th>
                      <th>
                        <span class="text-primary">Time</span>
                      </th>
                      <th v-for="day in dayColumns" :key="day.key">
                        <span class="text-primary">{{ day.label }}</span>
                      </th>
                    </tr>
                  </thead>

                  <tbody>
                    <tr
                      v-for="(schedule, index) in plan.schedules"
                      :key="`${plan.plan_type}-${index}`"
                      v-show="index < 2 || isPlanExpanded(plan.plan_type)"
                      :class="{
                        'row-conflict': conflictingRows.has(
                          `${plan.plan_type}-${index}`
                        ),
                      }"
                    >
                      <td>
                        <span class="text-primary">{{ index + 1 }}</span>
                      </td>

                      <td>
                        <v-switch
                          :model-value="schedule.enable"
                          color="success"
                          hide-details
                          density="compact"
                          @update:model-value="
                            toggleScheduleEnable(plan, schedule, index)
                          "
                        />
                      </td>

                      <td class="time-cell">
                        <v-select
                          v-model="schedule.time_minutes"
                          :items="timeOptions"
                          item-title="title"
                          item-value="value"
                          :disabled="!schedule.enable"
                          density="compact"
                          variant="outlined"
                          hide-details
                        />
                      </td>

                      <td v-for="day in dayColumns" :key="day.key">
                        <v-checkbox-btn
                          v-model="schedule.days[day.key]"
                          :disabled="!schedule.enable"
                          color="primary"
                        />
                      </td>
                    </tr>
                  </tbody>
                </v-table>

                <div
                  v-if="plan.schedules.length > 2"
                  class="d-flex justify-start mt-2"
                >
                  <v-btn
                    variant="text"
                    color="primary"
                    size="small"
                    @click="togglePlanExpanded(plan.plan_type)"
                  >
                    {{
                      isPlanExpanded(plan.plan_type) ? 'Show less' : 'Show more'
                    }}
                  </v-btn>
                </div>
              </v-expansion-panel-text>
            </v-expansion-panel>
          </v-expansion-panels>

          <v-card-actions class="px-0 pt-6">
            <v-spacer />
            <v-btn
              variant="outlined"
              color="primary"
              class="mr-3"
              @click="resetToDefaults"
            >
              Reset
            </v-btn>
            <v-btn
              color="primary"
              variant="flat"
              :loading="isSaving"
              :disabled="hasTimeConflicts"
              @click="saveSettings"
            >
              Save Settings
            </v-btn>
          </v-card-actions>
        </div>
      </v-card>
    </v-col>
  </v-row>
</template>

<script>
import { mapGetters } from 'vuex';
import API from '@/Core.Service.Domain/Controls/Thermostats/API';
import ImportScheduleDialog from '@/Core.Service.Domain/Controls/Thermostats/Components/ImportScheduleDialog.vue';

const PLAN_LABELS = {
  0: 'Wake',
  1: 'Away',
  2: 'Home',
  3: 'Sleep',
};

const PLAN_CONFIG = {
  0: {
    name: 'Wake',
    ruleKey: 'wake_event_rule_settings',
    scheduleKey: 'wake_schedule_settings',
  },
  1: {
    name: 'Away',
    ruleKey: 'away_event_rule_settings',
    scheduleKey: 'away_schedule_settings',
  },
  2: {
    name: 'Home',
    ruleKey: 'home_event_rule_settings',
    scheduleKey: 'home_schedule_settings',
  },
  3: {
    name: 'Sleep',
    ruleKey: 'sleep_event_rule_settings',
    scheduleKey: 'sleep_schedule_settings',
  },
};

// plan_target_temperature_unit is a DISPLAY preference, not the unit of the
// value next to it: the Milesight device always stores
// plan_target_temperature_value in Celsius regardless of what this field says.
// So '0' means "render this plan in Fahrenheit" and the stored value still has
// to be converted from Celsius on read (and back on write). Keep the two maps
// and the conversions in normalizeContractPlan/buildPayload in step.
const TEMPERATURE_UNIT_TO_API = {
  F: '0',
  C: '1',
};

const TEMPERATURE_UNIT_FROM_API = {
  0: 'F',
  1: 'C',
};

const dayColumns = [
  { key: 'sun', label: 'Sun' },
  { key: 'mon', label: 'Mon' },
  { key: 'tues', label: 'Tues' },
  { key: 'wed', label: 'Wed' },
  { key: 'thur', label: 'Thur' },
  { key: 'fri', label: 'Fri' },
  { key: 'sat', label: 'Sat' },
];

const temperatureControlModeOptions = [
  { title: 'Heat', value: 0 },
  { title: 'EM Heat', value: 1 },
  { title: 'Cool', value: 2 },
  { title: 'Auto', value: 3 },
];

const fanModeOptions = [
  { title: 'Auto', value: 0 },
  { title: 'On', value: 1 },
  { title: 'Circulate', value: 2 },
];

const temperatureUnitOptions = ['F', 'C'];

const celsiusToFahrenheit = (c) => Math.round((c * 9) / 5 + 32);
const fahrenheitToCelsius = (f) => Math.round(((f - 32) * 5) / 9);

const minutesToAmPm = (totalMinutes) => {
  const hour24 = Math.floor(totalMinutes / 60);
  const mins = String(totalMinutes % 60).padStart(2, '0');
  const period = hour24 < 12 ? 'AM' : 'PM';
  const hour12 = hour24 % 12 === 0 ? 12 : hour24 % 12;
  return `${hour12}:${mins} ${period}`;
};

const buildTimeOptions = () => {
  const options = [];

  for (let minutes = 0; minutes < 24 * 60; minutes += 15) {
    options.push({
      title: minutesToAmPm(minutes),
      value: minutes,
    });
  }

  return options;
};

const defaultScheduleRow = () => ({
  enable: false,
  time_minutes: 0,
  days: {
    sun: false,
    mon: false,
    tues: false,
    wed: false,
    thur: false,
    fri: false,
    sat: false,
  },
});

const defaultPlan = (planType) => ({
  plan_type: planType,
  label: PLAN_LABELS[planType],
  temperature_control_mode: 3,
  fan_mode: 0,
  target_temperature: 72,
  temperature_unit: 'F',
  temperature_tolerance: 3,
  schedules: Array.from({ length: 16 }, () => defaultScheduleRow()),
});

const mapApiDaysToUiDays = (schedule) => ({
  sun: Boolean(schedule.plan_loop_week_7),
  mon: Boolean(schedule.plan_loop_week_1),
  tues: Boolean(schedule.plan_loop_week_2),
  wed: Boolean(schedule.plan_loop_week_3),
  thur: Boolean(schedule.plan_loop_week_4),
  fri: Boolean(schedule.plan_loop_week_5),
  sat: Boolean(schedule.plan_loop_week_6),
});

const mapUiDaysToApiDays = (days) => ({
  plan_loop_week_7: Boolean(days.sun),
  plan_loop_week_1: Boolean(days.mon),
  plan_loop_week_2: Boolean(days.tues),
  plan_loop_week_3: Boolean(days.wed),
  plan_loop_week_4: Boolean(days.thur),
  plan_loop_week_5: Boolean(days.fri),
  plan_loop_week_6: Boolean(days.sat),
});

const buildDefaultPlans = () =>
  [0, 1, 2, 3].map((planType) => defaultPlan(planType));

const normalizeContractPlan = (contractItem, planType) => {
  const config = PLAN_CONFIG[planType];
  const fallback = defaultPlan(planType);

  if (!contractItem || typeof contractItem.schedule !== 'string') {
    return fallback;
  }

  try {
    const parsedSchedule = JSON.parse(contractItem.schedule);
    const ruleSettings = parsedSchedule[config.ruleKey] || {};
    const scheduleSettings = Array.isArray(parsedSchedule[config.scheduleKey])
      ? parsedSchedule[config.scheduleKey]
      : [];

    const temperature_unit =
      TEMPERATURE_UNIT_FROM_API[
        String(ruleSettings.plan_target_temperature_unit ?? '0')
      ] || 'F';

    const rawTemp = Number(
      ruleSettings.plan_target_temperature_value ?? fallback.target_temperature
    );

    const mappedPlan = {
      ...fallback,
      plan_type: planType,
      label: PLAN_LABELS[planType],
      temperature_control_mode: Number(
        ruleSettings.plan_temperature_control_mode ?? 3
      ),
      fan_mode: Number(ruleSettings.plan_fan_mode ?? 0),
      target_temperature:
        temperature_unit === 'F' ? celsiusToFahrenheit(rawTemp) : rawTemp,
      temperature_unit,
      temperature_tolerance: Number(
        ruleSettings.plan_temperature_control_tolerance ?? 3
      ),
      schedules: scheduleSettings.slice(0, 16).map((entry) => ({
        enable: Boolean(entry.plan_time_enable),
        time_minutes: Number(entry.plan_hour_time ?? 0),
        days: mapApiDaysToUiDays(entry),
      })),
    };

    while (mappedPlan.schedules.length < 16) {
      mappedPlan.schedules.push(defaultScheduleRow());
    }

    return mappedPlan;
  } catch (error) {
    console.error('Error normalizing contract plan:', error);
    return fallback;
  }
};

export default {
  name: 'ThermostatScheduleSettingsView',

  components: {
    ImportScheduleDialog,
  },

  // This view is reachable from two places — the schedule list page and the
  // config dialog on a LoRa tile — so remember which one, and return there.
  beforeRouteEnter(to, from, next) {
    next((vm) => {
      vm.originRouteName = from.name;
    });
  },

  data() {
    return {
      originRouteName: null,
      isLoading: true,
      isSaving: false,
      thermostatName: '',
      showImportDialog: false,
      plans: buildDefaultPlans(),
      openPlanPanels: [0, 1, 2, 3],
      planValidationErrors: {},
      expandedPlans: { 0: false, 1: false, 2: false, 3: false },
      dayColumns,
      timeOptions: buildTimeOptions(),
      temperatureControlModeOptions,
      fanModeOptions,
      temperatureUnitOptions,
    };
  },

  computed: {
    ...mapGetters({
      selectedSiteControls: 'ControlsStore/selectedSiteControls',
      defaultSite: 'session/defaultSite',
    }),

    thermostatId() {
      return Number(this.$route.params.thermostatId);
    },

    // Arriving from a LoRa tile means the user's mental "back" is the card
    // list, not the schedule list. Anything else (including a hard refresh,
    // where there is no previous route) keeps the original destination.
    cameFromThermostatsList() {
      return this.originRouteName === 'Thermostats';
    },

    backRoute() {
      return {
        name: this.cameFromThermostatsList
          ? 'Thermostats'
          : 'ThermostatSchedules',
      };
    },

    backLabel() {
      return this.cameFromThermostatsList
        ? 'Back to Thermostats'
        : 'Back to Schedules';
    },

    hasTimeConflicts() {
      const seen = new Set();
      for (const plan of this.plans) {
        for (const schedule of plan.schedules) {
          if (!schedule.enable) continue;
          for (const { key } of dayColumns) {
            if (schedule.days[key]) {
              const k = `${key}-${schedule.time_minutes}`;
              if (seen.has(k)) return true;
              seen.add(k);
            }
          }
        }
      }
      return false;
    },

    conflictingRows() {
      const seen = new Map();
      const conflicting = new Set();

      for (const plan of this.plans) {
        plan.schedules.forEach((schedule, rowIndex) => {
          if (!schedule.enable) return;
          for (const { key } of dayColumns) {
            if (schedule.days[key]) {
              const k = `${key}-${schedule.time_minutes}`;
              if (seen.has(k)) {
                const first = seen.get(k);
                conflicting.add(`${first.planType}-${first.rowIndex}`);
                conflicting.add(`${plan.plan_type}-${rowIndex}`);
              } else {
                seen.set(k, { planType: plan.plan_type, rowIndex });
              }
            }
          }
        });
      }

      return conflicting;
    },
  },

  watch: {
    plans: {
      deep: true,
      handler() {
        if (!Object.keys(this.planValidationErrors).length) return;
        const updated = { ...this.planValidationErrors };
        let changed = false;
        for (const plan of this.plans) {
          if (
            updated[plan.plan_type]?.length &&
            !this.validatePlan(plan).length
          ) {
            delete updated[plan.plan_type];
            changed = true;
          }
        }
        if (changed) this.planValidationErrors = updated;
      },
    },

    hasTimeConflicts: {
      handler(newVal, oldVal) {
        if (newVal && !oldVal) {
          this.$toast?.show?.({
            title: 'Schedule Conflict',
            message:
              'No two schedules can share the same time on the same day. Please review your plans before saving.',
            status: 'error',
          });
        }
      },
    },

    selectedSiteControls: {
      immediate: true,
      handler() {
        if (!this.thermostatName) {
          const selectedThermostat = this.selectedSiteControls.find(
            (control) => Number(control.id) === this.thermostatId
          );
          if (selectedThermostat) {
            this.thermostatName =
              selectedThermostat.friendlyName || selectedThermostat.name || '';
          }
        }
      },
    },
  },

  async created() {
    await this.loadSettings();
  },

  methods: {
    onUnitChange(plan, newUnit) {
      const oldUnit = plan.temperature_unit;
      if (oldUnit === newUnit) return;

      if (oldUnit === 'C' && newUnit === 'F') {
        plan.target_temperature = celsiusToFahrenheit(plan.target_temperature);
      } else if (oldUnit === 'F' && newUnit === 'C') {
        plan.target_temperature = fahrenheitToCelsius(plan.target_temperature);
      }

      plan.temperature_unit = newUnit;
    },

    isPlanExpanded(planType) {
      return this.expandedPlans[planType];
    },

    togglePlanExpanded(planType) {
      this.expandedPlans[planType] = !this.expandedPlans[planType];
    },

    toggleScheduleEnable(plan, schedule, index) {
      if (!schedule.enable) {
        schedule.enable = true;

        const hasExistingDays = dayColumns.some(
          ({ key }) => schedule.days[key]
        );

        if (!hasExistingDays) {
          // Fresh row — select all days and auto-pick a conflict-free time
          dayColumns.forEach(({ key }) => {
            schedule.days[key] = true;
          });

          // Find the nearest preceding enabled row and start one slot after it
          let baseMinutes = 0;
          for (let i = index - 1; i >= 0; i--) {
            if (plan.schedules[i].enable) {
              baseMinutes = (plan.schedules[i].time_minutes + 15) % (24 * 60);
              break;
            }
          }

          // Collect times already in use by other enabled rows in this plan
          const usedTimes = new Set(
            plan.schedules
              .filter((s) => s !== schedule && s.enable)
              .map((s) => s.time_minutes)
          );

          // Walk forward in 15-min steps until a free slot is found
          let candidate = baseMinutes;
          for (let i = 0; i < 96; i++) {
            if (!usedTimes.has(candidate)) break;
            candidate = (candidate + 15) % (24 * 60);
          }

          schedule.time_minutes = candidate;
        }
        // else: previously configured row — keep existing days and time
      } else {
        schedule.enable = false;
      }
    },

    toleranceError(plan) {
      const val = Number(plan.temperature_tolerance);
      if (val < 0.5 || val > 10) return 'Must be between 0.5 and 10.';
      return [];
    },

    temperatureError(plan) {
      const val = Number(plan.target_temperature);
      const isC = plan.temperature_unit === 'C';
      const min = isC ? 10 : 50;
      const max = isC ? 28 : 82;
      if (!Number.isInteger(val)) return 'Must be a whole number.';
      if (val < min || val > max)
        return `Must be between ${min} and ${max} °${plan.temperature_unit}.`;
      return [];
    },

    goBack() {
      this.$router.push(this.backRoute);
    },

    async handleImport({ thermostatId, action }) {
      this.isLoading = true;
      this.planValidationErrors = {};

      try {
        const response = await API.getThermostatScheduleSettings(thermostatId);
        const responsePlans = Array.isArray(response) ? response : [];

        this.plans = responsePlans.length
          ? [0, 1, 2, 3].map((planType) => {
              const incomingPlan = responsePlans.find(
                (plan) => Number(plan.planTypeId) === planType
              );
              return normalizeContractPlan(incomingPlan || null, planType);
            })
          : buildDefaultPlans();

        if (action === 'save') {
          this.isLoading = false;
          await this.saveSettings();
        } else {
          this.$toast?.show?.({
            title: 'Schedule Imported',
            message:
              'Schedule imported. Review the settings and save when ready.',
            status: 'success',
          });
        }
      } catch (error) {
        return Promise.reject(error);
      } finally {
        this.isLoading = false;
      }
    },

    resetSchedules() {
      this.plans = this.plans.map((plan) => ({
        ...plan,
        schedules: Array.from({ length: 16 }, () => defaultScheduleRow()),
      }));
      this.planValidationErrors = {};
    },

    resetToDefaults() {
      this.plans = buildDefaultPlans();
      this.planValidationErrors = {};
    },

    async loadSettings() {
      this.isLoading = true;
      this.planValidationErrors = {};

      try {
        const response = await API.getThermostatScheduleSettings(
          this.thermostatId
        );
        const responsePlans = Array.isArray(response) ? response : [];

        if (!responsePlans.length) {
          this.plans = buildDefaultPlans();
          return;
        }

        this.plans = [0, 1, 2, 3].map((planType) => {
          const incomingPlan = responsePlans.find(
            (plan) => Number(plan.planTypeId) === planType
          );

          return normalizeContractPlan(incomingPlan || null, planType);
        });
      } catch (error) {
        return Promise.reject(error);
      } finally {
        this.isLoading = false;
      }
    },

    validatePlan(plan) {
      const errors = [];
      const isUnitCelsius = plan.temperature_unit === 'C';
      const minTemp = isUnitCelsius ? 10 : 50;
      const maxTemp = isUnitCelsius ? 28 : 82;

      if (!Number.isInteger(Number(plan.target_temperature))) {
        errors.push(`${plan.label}: Target temperature must be an integer.`);
      }

      if (
        Number(plan.target_temperature) < minTemp ||
        Number(plan.target_temperature) > maxTemp
      ) {
        errors.push(
          `${plan.label}: Target temperature must be between ${minTemp} and ${maxTemp} ${plan.temperature_unit}.`
        );
      }

      if (
        Number(plan.temperature_tolerance) < 0.5 ||
        Number(plan.temperature_tolerance) > 10
      ) {
        errors.push(
          `${plan.label}: Temperature control tolerance must be between 0.5 and 10.`
        );
      }

      const seenByDayAndTime = new Set();

      plan.schedules.forEach((schedule, index) => {
        if (!schedule.enable) {
          return;
        }

        if (Number.isNaN(Number(schedule.time_minutes))) {
          errors.push(
            `${plan.label}: Row ${index + 1} must have a valid time.`
          );
          return;
        }

        const activeDays = dayColumns
          .map((day) => day.key)
          .filter((day) => Boolean(schedule.days[day]));

        if (!activeDays.length) {
          errors.push(
            `${plan.label}: Row ${index + 1} is enabled but has no day selected.`
          );
          return;
        }

        activeDays.forEach((day) => {
          const key = `${day}-${schedule.time_minutes}`;
          if (seenByDayAndTime.has(key)) {
            errors.push(
              `${plan.label}: Duplicate schedule detected for ${day.toUpperCase()} at ${this.formatMinutes(
                schedule.time_minutes
              )}.`
            );
          } else {
            seenByDayAndTime.add(key);
          }
        });
      });

      return errors;
    },

    validateAllPlans() {
      const validationMap = {};
      let hasErrors = false;

      this.plans.forEach((plan) => {
        const errors = this.validatePlan(plan);
        if (errors.length) {
          validationMap[plan.plan_type] = errors;
          hasErrors = true;
        }
      });

      this.planValidationErrors = validationMap;

      return !hasErrors;
    },

    formatMinutes(minutes) {
      return minutesToAmPm(Number(minutes));
    },

    buildPayload() {
      return this.plans.map((plan) => {
        const config = PLAN_CONFIG[plan.plan_type];
        const schedulePayload = {
          [config.ruleKey]: {
            plan_temperature_control_mode: String(
              Number(plan.temperature_control_mode)
            ),
            plan_fan_mode: String(Number(plan.fan_mode)),
            // Always Celsius on the wire — see TEMPERATURE_UNIT_FROM_API above.
            plan_target_temperature_value:
              plan.temperature_unit === 'F'
                ? fahrenheitToCelsius(Number(plan.target_temperature))
                : Number(plan.target_temperature),
            // '0' is the only plan_target_temperature_unit the device accepts;
            // sending '1' is rejected, so the C display preference cannot round
            // trip. Restore the mapped write once the vendor accepts '1'.
            // plan_target_temperature_unit: TEMPERATURE_UNIT_TO_API[plan.temperature_unit] || '0',
            plan_target_temperature_unit: '0',
            plan_temperature_control_tolerance: Number(
              plan.temperature_tolerance
            ),
          },
          [config.scheduleKey]: plan.schedules.slice(0, 16).map((schedule) => ({
            plan_time_enable: Boolean(schedule.enable),
            plan_hour_time: Number(schedule.time_minutes),
            ...mapUiDaysToApiDays(schedule.days || {}),
          })),
        };

        while (schedulePayload[config.scheduleKey].length < 16) {
          schedulePayload[config.scheduleKey].push({
            plan_time_enable: false,
            plan_hour_time: 0,
            ...mapUiDaysToApiDays(defaultScheduleRow().days),
          });
        }

        return {
          planTypeId: plan.plan_type,
          planTypeName: config.name,
          schedule: JSON.stringify(schedulePayload),
        };
      });
    },

    async saveSettings() {
      if (!this.validateAllPlans()) {
        return;
      }

      this.isSaving = true;

      try {
        const payload = this.buildPayload();
        await API.updateThermostatScheduleSettings(this.thermostatId, payload);

        this.$toast?.show?.({
          title: 'Schedule Updated',
          message: `${this.thermostatName || 'Thermostat'} schedule settings were saved successfully.`,
          status: 'success',
        });

        // Same destination as Back, so saving and cancelling don't land the
        // user in two different places.
        this.$router.push(this.backRoute);
      } catch (error) {
        return Promise.reject(error);
      } finally {
        this.isSaving = false;
      }
    },
  },
};
</script>

<style scoped>
.schedule-table {
  width: 100%;
  min-width: 980px;
}

.time-cell {
  min-width: 160px;
}

.row-conflict {
  background-color: rgba(var(--v-theme-error), 0.08) !important;
  outline: 1px solid rgba(var(--v-theme-error), 0.3);
  outline-offset: -1px;
}
</style>
